import { ResultConstant } from '@enums/result-constant.enum';
import { Base } from 'models/base.model';
import { serializable } from 'serializr';
import { ResultUpdateType } from '../enums/result-update-type.enum';
import { StatusConstant } from '../enums/status-constant.enum';

export class ResultBase extends Base {
  @serializable
  public startTime: Date;
  @serializable
  public endTime: Date;
  @serializable
  public duration: number;
  @serializable
  public result: ResultConstant;
  @serializable
  public status: StatusConstant;
  @serializable
  public started: boolean;
  @serializable
  public visualTestResult: ResultConstant;
  @serializable
  public totalCount: number;
  @serializable
  public passedCount: number;
  @serializable
  public failedCount: number;
  @serializable
  public stoppedCount: number;
  @serializable
  public notExecutedCount: number;
  @serializable
  public queuedCount: number;
  @serializable
  public runningCount: number;
  @serializable
  public skippedCount: number;
  @serializable
  public consolidatedPlanTotalCount: number;
  @serializable
  public consolidatedPlanPassedCount: number;
  @serializable
  public consolidatedPlanFailedCount: number;
  @serializable
  public consolidatedPlanStoppedCount: number;
  @serializable
  public consolidatedPlanNotExecutedCount: number;
  @serializable
  public consolidatedPlanQueuedCount: number;
  @serializable
  public consolidatedPlanRunningCount: number;
  @serializable
  public consolidatedPlanSkippedCount: number;
  @serializable
  public resultType: ResultUpdateType;
  @serializable
  public comments: string;
  @serializable
  public isDisabled: boolean;
  public childResultsPassedCount: number = 0;
  @serializable
  public isAfterSuiteFailed: boolean;

  get failedPercentage(): number {
    return Math.round(((this.failedCount || 0) / (this.totalCount || 1)) * 100);
  }

  get notExecutedPercentage(): number {
    return Math.round(
      ((this.notExecutedCount || 0) / (this.totalCount || 1)) * 100,
    );
  }

  get stoppedPercentage(): number {
    if (this.totalCount == 0 && this.isStopped) {
      return 100;
    }
    return Math.round(
      ((this.stoppedCount || 0) / (this.totalCount || 1)) * 100,
    );
  }

  get passedPercentage(): number {
    return Math.round(((this.passedCount || 0) / (this.totalCount || 1)) * 100);
  }

  get queuedPercentage(): number {
    if (this.totalCount == 0 && this.isQueued) {
      return 100;
    }
    return Math.round(((this.queuedCount || 0) / (this.totalCount || 1)) * 100);
  }

  get runningPercentage(): number {
    if (this.totalCount == 0 && this.isRunning) {
      return 100;
    }
    return Math.round(
      ((this.runningCount || 0) / (this.totalCount || 1)) * 100,
    );
  }

  get totalCountValue(): number {
    return this.totalCount;
  }

  get isStopped(): boolean {
    return this.result == ResultConstant.STOPPED;
  }

  get isPassed(): boolean {
    return this.result == ResultConstant.SUCCESS;
  }

  get isFailed(): boolean {
    return this.result == ResultConstant.FAILURE;
  }

  get isNotExecuted(): boolean {
    return this.result == ResultConstant.NOT_EXECUTED;
  }

  get isQueued(): boolean {
    return this.result == ResultConstant.QUEUED;
  }

  get isPaused(): boolean {
    return this.result == ResultConstant.PAUSED;
  }

  get isRunning(): boolean {
    return this.result == ResultConstant.RUNNING;
  }

  get isExecuting(): boolean {
    return (
      this.result == ResultConstant.RUNNING ||
      this.result == ResultConstant.QUEUED
    );
  }

  get hasExecutionFinished(): boolean {
    return !this.isExecuting;
  }

  get isOverridden(): boolean {
    return this.resultType == ResultUpdateType.MODIFIED;
  }

  get isVisualTestPassed(): boolean {
    return this.visualTestResult == ResultConstant.SUCCESS;
  }
  get isVisualTestFailed(): boolean {
    return this.visualTestResult == ResultConstant.FAILURE;
  }

  get isVisualTestRunning(): boolean {
    return this.visualTestResult == ResultConstant.RUNNING;
  }

  getCountsArray() {
    const data = [];
    data.push(this.passedCount || 0);
    data.push(this.failedCount || 0);
    data.push(this.stoppedCount || 0);
    data.push(this.queuedCount || 0);
    data.push(this.notExecutedCount || 0);
    return data;
  }
}
